import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, CheckCircle, RotateCcw, Wrench, CornerDownRight, MoreHorizontal, Pencil, Trash2, Quote, X, ShieldCheck, ShieldAlert } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import type { TextSelection } from "@/lib/comment-utils";

interface PolicyComment {
  id: string;
  policy_id: string;
  company_id: string;
  created_by: string;
  parent_comment_id: string | null;
  content: string;
  selected_text: string | null;
  selection_start: number | null;
  selection_end: number | null;
  selection_context: string | null;
  comment_type: "general" | "revision_request";
  is_resolved: boolean;
  resolved_by: string | null;
  resolved_at: string | null;
  resolution_note: string | null;
  created_at: string;
  updated_at: string;
  creator?: { id: string; name: string; email: string } | null;
}

interface PolicyCommentsProps {
  policyId: string;
  assignedReviewerIds?: string[];
  assignedApproverIds?: string[];
  canBackToRework?: boolean;
  onBackToRework?: (sourceCommentId: string, reason?: string) => Promise<void>;
  pendingSelection?: TextSelection | null;
  onClearPendingSelection?: () => void;
}

function Avatar({ name, size = "md" }: { name: string; size?: "sm" | "md" }) {
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const colors = [
    "bg-violet-500", "bg-blue-500", "bg-emerald-500",
    "bg-amber-500", "bg-rose-500", "bg-cyan-500",
  ];
  const color = colors[name.charCodeAt(0) % colors.length];
  return (
    <div className={cn(
      "rounded-full flex items-center justify-center text-white font-medium shrink-0",
      color,
      size === "sm" ? "w-6 h-6 text-[10px]" : "w-8 h-8 text-xs"
    )}>
      {initials}
    </div>
  );
}

export function PolicyComments({
  policyId,
  assignedReviewerIds = [],
  assignedApproverIds = [],
  canBackToRework = false,
  onBackToRework,
  pendingSelection,
  onClearPendingSelection,
}: PolicyCommentsProps) {
  const [comments, setComments] = useState<PolicyComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [isBusy, setIsBusy] = useState(false);

  const [composeText, setComposeText] = useState("");
  const [composeFocused, setComposeFocused] = useState(false);
  const [composeType, setComposeType] = useState<"general" | "revision_request">("general");

  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");

  const replyInputRef = useRef<HTMLTextAreaElement>(null);
  const composeRef = useRef<HTMLTextAreaElement>(null);
  const { profile, hasAnyRole } = useAuth();
  const { toast } = useToast();

  const fetchComments = useCallback(async () => {
    if (!profile?.company_id || !policyId) return;
    try {
      const { data, error } = await supabase
        .from("policy_comments")
        .select(`*, creator:profiles!policy_comments_created_by_fkey(id, name, email)`)
        .eq("policy_id", policyId)
        .eq("company_id", profile.company_id)
        .order("created_at", { ascending: true });
      if (error) throw error;
      setComments((data || []) as PolicyComment[]);
    } catch {
      toast({ title: "Error", description: "Failed to fetch comments", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [policyId, profile?.company_id, toast]);

  useEffect(() => {
    if (!policyId || !profile?.company_id) return;
    void fetchComments();
    const channel = supabase
      .channel(`policy-comments-${policyId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "policy_comments", filter: `policy_id=eq.${policyId}` }, () => { void fetchComments(); })
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [fetchComments, policyId, profile?.company_id]);

  useEffect(() => {
    if (replyingToId && replyInputRef.current) {
      replyInputRef.current.focus();
    }
  }, [replyingToId]);

  useEffect(() => {
    if (pendingSelection) {
      setComposeFocused(true);
      setTimeout(() => composeRef.current?.focus(), 50);
    }
  }, [pendingSelection]);

  const isPolicyModerator = useMemo(() => {
    if (!profile) return false;
    return hasAnyRole(["admin"]) || assignedReviewerIds.includes(profile.id) || assignedApproverIds.includes(profile.id);
  }, [assignedApproverIds, assignedReviewerIds, hasAnyRole, profile]);

  const { rootComments, repliesByParent } = useMemo(() => {
    const roots = comments.filter((c) => !c.parent_comment_id);
    const map = new Map<string, PolicyComment[]>();
    comments.filter((c) => c.parent_comment_id).forEach((r) => {
      const list = map.get(r.parent_comment_id!) || [];
      list.push(r);
      map.set(r.parent_comment_id!, list);
    });
    return { rootComments: roots, repliesByParent: map };
  }, [comments]);

  const resolvedCount = useMemo(() => rootComments.filter((c) => c.is_resolved).length, [rootComments]);
  const openRevisions = useMemo(() => rootComments.filter((c) => c.comment_type === "revision_request" && !c.is_resolved).length, [rootComments]);

  // --- Actions ---

  const handlePost = async () => {
    if (!profile || !composeText.trim()) return;
    setIsBusy(true);
    try {
      const { error } = await supabase.from("policy_comments").insert({
        policy_id: policyId,
        company_id: profile.company_id,
        created_by: profile.id,
        content: composeText.trim(),
        comment_type: composeType,
        ...(pendingSelection ? {
          selected_text: pendingSelection.text,
          selection_start: pendingSelection.startIndex,
          selection_end: pendingSelection.endIndex,
          selection_context: pendingSelection.context,
        } : {}),
      });
      if (error) throw error;
      setComposeText("");
      setComposeFocused(false);
      onClearPendingSelection?.();
      await fetchComments();
    } catch {
      toast({ title: "Error", description: "Failed to post comment", variant: "destructive" });
    } finally {
      setIsBusy(false);
    }
  };

  const handleReply = async (parentId: string) => {
    if (!profile || !replyText.trim()) return;
    const root = comments.find((c) => c.id === parentId);
    if (!root) return;
    setIsBusy(true);
    try {
      const { error } = await supabase.from("policy_comments").insert({
        policy_id: policyId,
        company_id: profile.company_id,
        created_by: profile.id,
        parent_comment_id: root.id,
        content: replyText.trim(),
        comment_type: root.comment_type,
      });
      if (error) throw error;
      setReplyingToId(null);
      setReplyText("");
      await fetchComments();
    } catch {
      toast({ title: "Error", description: "Failed to post reply", variant: "destructive" });
    } finally {
      setIsBusy(false);
    }
  };

  const handleSaveEdit = async (id: string) => {
    if (!editText.trim()) return;
    setIsBusy(true);
    try {
      const { error } = await supabase.from("policy_comments").update({ content: editText.trim() }).eq("id", id);
      if (error) throw error;
      setEditingId(null);
      await fetchComments();
    } catch {
      toast({ title: "Error", description: "Failed to edit comment", variant: "destructive" });
    } finally {
      setIsBusy(false);
    }
  };

  const handleDelete = async (id: string) => {
    setIsBusy(true);
    try {
      const { error } = await supabase.from("policy_comments").delete().eq("id", id);
      if (error) throw error;
      await fetchComments();
    } catch {
      toast({ title: "Error", description: "Failed to delete comment", variant: "destructive" });
    } finally {
      setIsBusy(false);
    }
  };

  const handleToggleResolve = async (commentId: string, currentStatus: boolean) => {
    if (!profile) return;
    setIsBusy(true);
    try {
      const next = !currentStatus;
      const now = new Date().toISOString();
      const { error } = await supabase
        .from("policy_comments")
        .update({
          is_resolved: next,
          resolved_by: next ? profile.id : null,
          resolved_at: next ? now : null,
          resolution_note: null,
        })
        .or(`id.eq.${commentId},parent_comment_id.eq.${commentId}`);
      if (error) throw error;
      toast({ title: next ? "Thread resolved" : "Thread re-opened" });
      await fetchComments();
    } catch {
      toast({ title: "Error", description: "Failed to update thread", variant: "destructive" });
    } finally {
      setIsBusy(false);
    }
  };

  const handleBackToRework = async (comment: PolicyComment) => {
    if (!onBackToRework || !canBackToRework) return;
    setIsBusy(true);
    try {
      await onBackToRework(comment.id, comment.content);
      await handleToggleResolve(comment.id, false);
      toast({ title: "Sent to Rework", description: "Policy moved back to draft." });
    } catch {
      toast({ title: "Error", description: "Failed to send back to rework", variant: "destructive" });
    } finally {
      setIsBusy(false);
    }
  };

  // --- Render helpers ---

  const CommentBubble = ({ comment, isReply = false }: { comment: PolicyComment; isReply?: boolean }) => {
    const isOwn = profile?.id === comment.created_by;
    const canResolve = !isReply && !!profile && (isOwn || isPolicyModerator);
    const canEdit = isOwn;
    const canDelete = isOwn || isPolicyModerator;
    const isEditing = editingId === comment.id;
    const name = comment.creator?.name || "Unknown";

    return (
      <div className={cn("flex gap-2.5 group", isReply && "ml-8 mt-2")}>
        <Avatar name={name} size={isReply ? "sm" : "md"} />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-sm font-medium leading-none">{name}</span>
            <span className="text-xs text-muted-foreground">
              {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
            </span>
            {!isReply && comment.comment_type === "revision_request" && (
              <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-4">Revision</Badge>
            )}
            {!isReply && comment.is_resolved && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 gap-0.5">
                <CheckCircle className="w-2.5 h-2.5" />Resolved
              </Badge>
            )}
          </div>

          {comment.selected_text && !isEditing && (
            <div className="flex items-start gap-1.5 rounded border-l-2 border-primary/30 bg-muted/40 pl-2.5 pr-2 py-1.5 mb-1.5">
              <Quote className="w-3 h-3 text-muted-foreground mt-0.5 shrink-0" />
              <p className="text-xs text-muted-foreground italic line-clamp-2">{comment.selected_text}</p>
            </div>
          )}

          {isEditing ? (
            <div className="space-y-2">
              <Textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                className="text-sm min-h-[64px] resize-none"
                autoFocus
              />
              <div className="flex gap-2">
                <Button size="sm" disabled={isBusy} onClick={() => void handleSaveEdit(comment.id)}>Save</Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
              </div>
            </div>
          ) : (
            <p className={cn("text-sm leading-relaxed", comment.is_resolved && "opacity-60")}>
              {comment.content}
            </p>
          )}

          {!isEditing && (
            <div className="flex items-center gap-3 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
              {!isReply && (
                <button
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                  onClick={() => {
                    setReplyingToId(replyingToId === comment.id ? null : comment.id);
                    setReplyText("");
                  }}
                >
                  <CornerDownRight className="w-3 h-3" />
                  Reply
                </button>
              )}
              {canResolve && (
                <button
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                  disabled={isBusy}
                  onClick={() => void handleToggleResolve(comment.id, comment.is_resolved)}
                >
                  {comment.is_resolved ? (
                    <><RotateCcw className="w-3 h-3" />Re-open</>
                  ) : (
                    <><CheckCircle className="w-3 h-3" />Resolve</>
                  )}
                </button>
              )}
              {canBackToRework && onBackToRework && !isReply && comment.comment_type === "revision_request" && !comment.is_resolved && (
                <button
                  className="text-xs text-destructive hover:text-destructive/80 transition-colors flex items-center gap-1"
                  disabled={isBusy}
                  onClick={() => void handleBackToRework(comment)}
                >
                  <Wrench className="w-3 h-3" />
                  Back to Rework
                </button>
              )}
              {(canEdit || canDelete) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                      <MoreHorizontal className="w-3.5 h-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="text-sm">
                    {canEdit && (
                      <DropdownMenuItem onClick={() => { setEditingId(comment.id); setEditText(comment.content); }}>
                        <Pencil className="w-3.5 h-3.5 mr-2" />Edit
                      </DropdownMenuItem>
                    )}
                    {canDelete && (
                      <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => void handleDelete(comment.id)}>
                        <Trash2 className="w-3.5 h-3.5 mr-2" />Delete
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Card className="shadow-card">
        <CardContent className="py-8 text-center">
          <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <MessageSquare className="w-4 h-4" />
            Comments
            {comments.length > 0 && <span className="text-muted-foreground font-normal">({comments.length})</span>}
          </CardTitle>
          <div className="flex items-center gap-2">
            {openRevisions > 0 && (
              <Badge variant="destructive" className="text-xs">{openRevisions} revision{openRevisions > 1 ? 's' : ''}</Badge>
            )}
            {resolvedCount > 0 && (
              <Badge variant="secondary" className="text-xs">{resolvedCount} resolved</Badge>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4 pt-0">
        {/* Compose box */}
        <div className="flex gap-2.5">
          <Avatar name={profile?.name || "You"} />
          <div className="flex-1 space-y-2">
            {/* Quoted selection */}
            {pendingSelection && (
              <div className="flex items-start gap-2 rounded-md bg-muted/60 border-l-2 border-primary/40 pl-3 pr-2 py-2">
                <Quote className="w-3 h-3 text-muted-foreground mt-0.5 shrink-0" />
                <p className="text-xs text-muted-foreground italic flex-1 line-clamp-2">
                  {pendingSelection.text}
                </p>
                <button
                  className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
                  onClick={() => onClearPendingSelection?.()}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
            <Textarea
              ref={composeRef}
              placeholder={pendingSelection ? "Comment on the selected text…" : "Write a comment…"}
              value={composeText}
              onChange={(e) => setComposeText(e.target.value)}
              onFocus={() => setComposeFocused(true)}
              className="resize-none text-sm min-h-[38px] transition-all"
              rows={composeFocused || composeText || pendingSelection ? 3 : 1}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  void handlePost();
                }
              }}
            />
            {(composeFocused || composeText) && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1 rounded-md border p-0.5 text-xs">
                  <button
                    className={cn("px-2 py-0.5 rounded transition-colors", composeType === "general" ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground")}
                    onClick={() => setComposeType("general")}
                  >
                    General
                  </button>
                  <button
                    className={cn("px-2 py-0.5 rounded transition-colors", composeType === "revision_request" ? "bg-destructive/10 text-destructive" : "text-muted-foreground hover:text-foreground")}
                    onClick={() => setComposeType("revision_request")}
                  >
                    Revision
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setComposeFocused(false); setComposeText(""); }}>
                    Cancel
                  </Button>
                  <Button size="sm" className="h-7 text-xs" disabled={isBusy || !composeText.trim()} onClick={() => void handlePost()}>
                    Post
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Thread list */}
        {rootComments.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No comments yet</p>
          </div>
        ) : (
          <div className="space-y-4 divide-y divide-border">
            {rootComments.map((comment) => {
              const replies = repliesByParent.get(comment.id) || [];
              return (
                <div key={comment.id} className="pt-4 first:pt-0">
                  <CommentBubble comment={comment} />

                  {/* Replies */}
                  {replies.length > 0 && (
                    <div className="space-y-3 mt-3">
                      {replies.map((reply) => (
                        <CommentBubble key={reply.id} comment={reply} isReply />
                      ))}
                    </div>
                  )}

                  {/* Reply compose */}
                  {replyingToId === comment.id && (
                    <div className="ml-8 mt-3 flex gap-2">
                      <Avatar name={profile?.name || "You"} size="sm" />
                      <div className="flex-1 space-y-2">
                        <Textarea
                          ref={replyInputRef}
                          placeholder={`Reply to ${comment.creator?.name || "comment"}…`}
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          className="resize-none text-sm min-h-[64px]"
                          rows={2}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                              e.preventDefault();
                              void handleReply(comment.id);
                            }
                            if (e.key === "Escape") setReplyingToId(null);
                          }}
                        />
                        <div className="flex gap-2 justify-end">
                          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setReplyingToId(null)}>Cancel</Button>
                          <Button size="sm" className="h-7 text-xs" disabled={isBusy || !replyText.trim()} onClick={() => void handleReply(comment.id)}>Reply</Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {/* Approval Conditions */}
        {rootComments.some((c) => c.comment_type === "revision_request") && (
          <div className="rounded-lg border bg-muted/30 p-3 space-y-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {openRevisions === 0 ? (
                  <ShieldCheck className="w-4 h-4 text-emerald-500" />
                ) : (
                  <ShieldAlert className="w-4 h-4 text-destructive" />
                )}
                <span className="text-sm font-medium">Approval Conditions</span>
              </div>
              {openRevisions === 0 ? (
                <Badge variant="secondary" className="text-xs gap-1 text-emerald-600 dark:text-emerald-400">
                  <CheckCircle className="w-3 h-3" />
                  All clear
                </Badge>
              ) : (
                <Badge variant="destructive" className="text-xs">
                  {openRevisions} blocking
                </Badge>
              )}
            </div>

            {openRevisions > 0 ? (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Resolve the following before approval:
                </p>
                {rootComments
                  .filter((c) => c.comment_type === "revision_request" && !c.is_resolved)
                  .map((c) => (
                    <div key={c.id} className="flex items-start gap-2.5 rounded-md border border-destructive/20 bg-destructive/5 p-2.5">
                      <div className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-destructive" />
                      <div className="min-w-0 flex-1">
                        <p className="line-clamp-2 text-xs leading-relaxed">{c.content}</p>
                        <p className="mt-0.5 text-[10px] text-muted-foreground">
                          {c.creator?.name || "Unknown"} · {formatDistanceToNow(new Date(c.created_at), { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                All {rootComments.filter((c) => c.comment_type === "revision_request").length} revision request{rootComments.filter((c) => c.comment_type === "revision_request").length !== 1 ? "s" : ""} resolved — policy can be approved.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
